import { useState } from 'react';

import { DocumentPreviewModal } from './DocumentPreviewModal';

import type { AttachedDocument } from '../../../lib/types';

interface MessageAttachmentProps {
  attached: AttachedDocument;
}

/**
 * MessageAttachment — Item 36 PR-B2.
 *
 * Card de anexo renderizado dentro da bolha de mensagem quando contentType=document.
 * Click → abre DocumentPreviewModal (PDF iframe + imagem lightbox + outros download).
 *
 * Item 236 (29/Mai/2026) — quando `signedUrl` está undefined, o card está em
 * estado otimista (servidor ainda não confirmou a mensagem). Nesse caso o
 * click no card NÃO abre o preview (evita iframe vazio); título/aria reflete
 * "Enviando…" para feedback discreto ao atendente.
 */
export function MessageAttachment({ attached }: MessageAttachmentProps): JSX.Element {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isOptimistic = attached.signedUrl === undefined;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Gate optimistic: sem signedUrl, click não abre preview.
          if (isOptimistic) return;
          setPreviewOpen(true);
        }}
        aria-label={isOptimistic ? 'Enviando anexo…' : 'Abrir preview do anexo'}
        title={isOptimistic ? 'Enviando…' : 'Abrir preview'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: 8,
          background: 'var(--bg-surface-2)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 6,
          cursor: isOptimistic ? 'progress' : 'pointer',
          width: '100%',
          textAlign: 'left',
          opacity: isOptimistic ? 0.7 : 1,
        }}
      >
        <DocIcon mime={attached.mimeType} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500 }}>{attached.name}</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            {formatBytes(attached.sizeBytes)}
            {isOptimistic ? ' · enviando…' : ' · clique para abrir'}
          </div>
        </div>
      </button>
      {/* Só renderiza Preview quando signedUrl está disponível (pós-onSuccess). */}
      {!isOptimistic && (
        <DocumentPreviewModal
          attached={attached}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

function DocIcon({ mime }: { mime: string }): JSX.Element {
  if (mime === 'application/pdf')
    return (
      <span aria-hidden style={{ fontSize: 24 }}>
        📄
      </span>
    );
  if (mime.startsWith('image/'))
    return (
      <span aria-hidden style={{ fontSize: 24 }}>
        🖼
      </span>
    );
  if (mime.startsWith('video/'))
    return (
      <span aria-hidden style={{ fontSize: 24 }}>
        🎬
      </span>
    );
  if (mime.startsWith('audio/'))
    return (
      <span aria-hidden style={{ fontSize: 24 }}>
        🎵
      </span>
    );
  return (
    <span aria-hidden style={{ fontSize: 24 }}>
      📎
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
